import React from 'react';
import PropTypes from 'prop-types';
import { isBoolean } from 'lodash';
import { Skeleton, Typography } from '@metarouter-private/mr-mui';
import { FIELD_VALUE_STATES, UNKNOWN_REASONS } from '@ion/api/audit-logs/format';

import ComplexValueDisclosure from './ComplexValueDisclosure';
import UNKNOWN_REASON_TEXT from './unknownReasonText';
import visuallyHiddenStyle from './visuallyHiddenStyle';

export default function FieldValueView({ fieldValue, complex }) {
  if (fieldValue.state === FIELD_VALUE_STATES.VALUE) {
    if (complex) {
      return (
        <ComplexValueDisclosure
          value={fieldValue.value}
          truncated={fieldValue.truncated}
          testId="auditLogComplexValue"
        />
      );
    }
    if (fieldValue.truncated) {
      return <ComplexValueDisclosure value={fieldValue.value} truncated testId="auditLogTruncatedValue" />;
    }
    return <Typography variant="body2">{toDisplayText(fieldValue.value)}</Typography>;
  }

  if (fieldValue.state === FIELD_VALUE_STATES.EMPTY) {
    return (
      <Typography data-test-id="auditLogEmptyValue" variant="body2" color="text.secondary">
        (empty)
      </Typography>
    );
  }

  if (fieldValue.state === FIELD_VALUE_STATES.LOADING) {
    return (
      <Typography data-test-id="auditLogLoadingValue" variant="body2">
        <Skeleton aria-hidden="true" width="6em" />
        <span style={visuallyHiddenStyle}>Loading previous value</span>
      </Typography>
    );
  }

  if (fieldValue.reason === UNKNOWN_REASONS.NO_PREDECESSOR) {
    return (
      <Typography data-test-id="auditLogUnknownValue" variant="body2" color="text.secondary" fontStyle="italic">
        no previous value
      </Typography>
    );
  }

  return (
    <Typography data-test-id="auditLogUnknownValue" variant="body2" color="text.secondary" fontStyle="italic">
      unknown
      <span data-test-id="auditLogUnknownReason" style={visuallyHiddenStyle}>
        {UNKNOWN_REASON_TEXT[fieldValue.reason]}
      </span>
    </Typography>
  );
}

FieldValueView.propTypes = {
  fieldValue: PropTypes.shape({
    state: PropTypes.oneOf(Object.values(FIELD_VALUE_STATES)).isRequired,
    value: PropTypes.any,
    truncated: PropTypes.bool,
    reason: PropTypes.oneOf(Object.values(UNKNOWN_REASONS)),
  }).isRequired,
  complex: PropTypes.bool,
};

FieldValueView.defaultProps = {
  complex: false,
};

// React renders a boolean as nothing, so a true/false value would leave the cell blank — visually
// indistinguishable from a field that was never recorded, which is exactly the distinction between
// empty, unknown and a value exists to preserve. Yes/No is the human-first form.
function toDisplayText(value) {
  if (isBoolean(value)) return value ? 'Yes' : 'No';
  return value;
}
